import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';

interface LanguageToggleProps {
  className?: string;
  variant?: 'nav' | 'inline' | 'dashboardSidebar' | 'dashboardTopBar';
  collapsed?: boolean;
}

function useLanguageState() {
  const { i18n, t } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2) as SupportedLanguage;
  const cycle = () => {
    const idx = SUPPORTED_LANGUAGES.indexOf(current);
    const next = SUPPORTED_LANGUAGES[(idx + 1) % SUPPORTED_LANGUAGES.length];
    i18n.changeLanguage(next);
  };
  const fullLabel = current === 'es' ? t('common.spanish') : t('common.english');
  return { current, cycle, fullLabel, t };
}

export default function LanguageToggle({
  className = '',
  variant = 'nav',
  collapsed = false,
}: LanguageToggleProps) {
  const { cycle, fullLabel, t } = useLanguageState();

  // Customer top-nav (desktop) — globe + full language name, yellow hover
  if (variant === 'nav') {
    return (
      <button
        onClick={cycle}
        className={`flex items-center gap-1.5 text-sm font-medium text-white hover:text-yellow-400 transition-colors ${className}`}
        aria-label={t('common.language')}
        title={t('common.language')}
      >
        <Globe className="w-4 h-4" />
        <span>{fullLabel}</span>
      </button>
    );
  }

  // Customer mobile-menu (collapsed hamburger) — left-aligned row
  if (variant === 'inline') {
    return (
      <button
        onClick={cycle}
        className={`flex items-center gap-2 text-sm font-medium text-white hover:text-yellow-400 transition-colors ${className}`}
        aria-label={t('common.language')}
      >
        <Globe className="w-4 h-4" />
        <span>{fullLabel}</span>
      </button>
    );
  }

  // Business dashboard sidebar — matches Sign Out / Back to Zap row pattern
  if (variant === 'dashboardSidebar') {
    return (
      <button
        onClick={cycle}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-dash-md text-sm text-dash-text-muted hover:text-dash-text-primary hover:bg-dash-bg-elevated transition-colors w-full ${
          collapsed ? 'justify-center' : ''
        } ${className}`}
        title={t('common.language')}
      >
        <Globe size={18} className="shrink-0" />
        {!collapsed && <span>{fullLabel}</span>}
      </button>
    );
  }

  // Business dashboard mobile top bar — compact pill
  return (
    <button
      onClick={cycle}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-dash-md text-xs font-medium text-dash-text-secondary hover:text-dash-text-primary hover:bg-dash-bg-elevated transition-colors ${className}`}
      aria-label={t('common.language')}
      title={t('common.language')}
    >
      <Globe size={16} />
      <span>{fullLabel}</span>
    </button>
  );
}
